import GameObject from '../../GameObject/GameObject';
import TransformComponent from '../../Component/Transform';
import AnimationClip from './AnimationClip';
import { Easing, Tween } from '@tweenjs/tween.js';
import Renderable from '../../Component/Renderable';
import { Point } from 'pixi.js';

export type TweenAnimationTypes = TransformComponent | Renderable;

interface ICreateTween<SpecificProps> extends ITweenDefault<SpecificProps> {
    animationClip: AnimationClip;
    targetObject: GameObject;
}

interface ITweenDefault<SpecificProps> {
    duration: number;
    delay?: number;
    easing?(K: number): number;
    meta?: SpecificProps;
    yoyo?: boolean;
}

/**
 * returns the basic props to create a tween
 * @param targetObject {GameObject}
 * @param animationClip {AnimationClip}
 * @param meta {GenericType: props you want to animate}
 */
export const getTweenProps = <Props>(
    targetObject: GameObject,
    animationClip: AnimationClip,
    meta: Props
) => {
    return {
        targetObject,
        animationClip,
        duration: 800,
        meta,
    };
};

/**
 * Return a scale tween animation
 * @param targetObject {GameObject}
 * @param animationClip {AnimationClip}
 * @param tweenProps {ICreateTween<GenericType>}
 */
export const createScaleTween = ({
    targetObject,
    animationClip,
    delay = 0,
    easing = Easing.Quartic.InOut,
    duration,
    meta,
    yoyo = false,
}: ICreateTween<Point>): Tween => {
    const transform = targetObject.getComponent(TransformComponent);
    if (!transform) throw 'the object does not have transform component';

    const tween = animationClip.getTween(transform.scale);
    tween
        .to(
            {
                ...meta,
            },
            duration
        )
        .delay(delay)
        .easing(easing)
        .yoyo(yoyo);

    return tween;
};

/**
 * Return an alpha tween animation
 * @param targetObject {GameObject}
 * @param animationClip {AnimationClip}
 * @param tweenProps {ICreateTween<GenericType>}
 */
export const createAlphaTween = ({
    targetObject,
    animationClip,
    delay = 0,
    easing = Easing.Quartic.InOut,
    duration,
    meta,
    yoyo = false,
}: ICreateTween<{ alpha: number }>): Tween => {
    let renderable = targetObject.getComponent(Renderable);
    if (!renderable) {
        renderable = targetObject.addComponent(Renderable);
    }

    const tween = animationClip.getTween(renderable.container);
    tween
        .to(
            {
                ...meta,
            },
            duration
        )
        .delay(delay)
        .easing(easing)
        .yoyo(yoyo);

    return tween;
};

/**
 * Return a position tween animation
 * @param targetObject {GameObject}
 * @param animationClip {AnimationClip}
 * @param tweenProps {ICreateTween<GenericType>}
 */
export const createPositionTween = ({
    targetObject,
    animationClip,
    delay = 0,
    easing = Easing.Quartic.InOut,
    duration,
    meta,
    yoyo = false,
}: ICreateTween<Point>): Tween => {
    const transform = targetObject.getComponent(TransformComponent);
    if (!transform) throw 'the object does not have transform component';

    const tween = animationClip.getTween(transform);
    tween
        .to(
            {
                ...meta,
            },
            duration
        )
        .delay(delay)
        .easing(easing)
        .yoyo(yoyo);

    return tween;
};

/**
 * Return a rotation tween animation
 * @param targetObject {GameObject}
 * @param animationClip {AnimationClip}
 * @param tweenProps {ICreateTween<GenericType>}
 */
export const createRotationTween = ({
    targetObject,
    animationClip,
    delay = 0,
    easing = Easing.Quartic.InOut,
    duration,
    meta,
}: ICreateTween<number>): Tween => {
    const transform = targetObject.getComponent(TransformComponent);
    if (!transform) throw 'the object does not have transform component';
    const tween = animationClip.getTween(transform);
    tween
        .to(
            {
                rotation: meta,
            },
            duration
        )
        .delay(delay)
        .easing(easing);

    return tween;
};
